import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useGlobal } from '../../../../Core/GlobalState/Store';
import { computeLayout } from '../../../../Core/Services/CanvasLayoutService';
import MapStage from './MapStage';
import ZoomControls from './ZoomControls';
import MapTooltip from './MapTooltip';
import MapLegend from './MapLegend';

const MIN_SCALE = 0.3;
const MAX_SCALE = 3;
const ZOOM_STEP = 1.15;
const STAGE_MIN_HEIGHT = 350;

export default function CanvasMap({
  firstLayer,
  setSelectedLevel,
  setSelectedSizeType,
}) {
  const {
    room: selectedRoom,
    setRoom: setSelectedRoom,
    sizeTypes,
    selectedSize,
  } = useGlobal();

  const containerRef = useRef(null);
  const lastTouchDist = useRef(null);
  const lastTouchCenter = useRef(null);

  const [stageSize, setStageSize] = useState({
    width: 600,
    height: STAGE_MIN_HEIGHT,
  });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    room: null,
  });

  // Compute layout from grid data
  const layout = useMemo(
    () => computeLayout(firstLayer, selectedSize, selectedRoom),
    [firstLayer, selectedSize, selectedRoom]
  );

  // Responsive stage sizing
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      if (width > 0) {
        setStageSize({
          width,
          height: Math.max(STAGE_MIN_HEIGHT, width * 0.65),
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Fit-to-view on first meaningful render
  const hasFitted = useRef(false);
  useEffect(() => {
    if (layout.totalWidth > 0 && stageSize.width > 0 && !hasFitted.current) {
      hasFitted.current = true;
      fitToView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.totalWidth, stageSize.width]);

  // Reset fit flag when layer changes
  useEffect(() => {
    hasFitted.current = false;
  }, [firstLayer]);

  const clampScale = s => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));

  const fitToView = useCallback(() => {
    if (layout.totalWidth === 0 || layout.totalHeight === 0) return;
    const padding = 20;
    const fitScale = Math.min(
      (stageSize.width - padding * 2) / layout.totalWidth,
      (stageSize.height - padding * 2) / layout.totalHeight,
      1.5
    );
    const clamped = clampScale(fitScale);
    setScale(clamped);
    setPosition({
      x: (stageSize.width - layout.totalWidth * clamped) / 2,
      y: (stageSize.height - layout.totalHeight * clamped) / 2,
    });
  }, [layout.totalWidth, layout.totalHeight, stageSize]);

  // Mouse wheel zoom toward pointer
  const handleWheel = useCallback(
    e => {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const oldScale = scale;
      const newScale = clampScale(
        e.evt.deltaY < 0 ? oldScale * ZOOM_STEP : oldScale / ZOOM_STEP
      );

      const mousePointTo = {
        x: (pointer.x - position.x) / oldScale,
        y: (pointer.y - position.y) / oldScale,
      };

      setScale(newScale);
      setPosition({
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      });
    },
    [scale, position]
  );

  // Drag end
  const handleDragEnd = useCallback(e => {
    setPosition({ x: e.target.x(), y: e.target.y() });
  }, []);

  // Touch pinch-to-zoom
  const handleTouchMove = useCallback(
    e => {
      const touches = e.evt.touches;
      if (touches.length !== 2) {
        lastTouchDist.current = null;
        lastTouchCenter.current = null;
        return;
      }
      e.evt.preventDefault();

      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const centerX = (touches[0].clientX + touches[1].clientX) / 2;
      const centerY = (touches[0].clientY + touches[1].clientY) / 2;

      if (lastTouchDist.current != null) {
        const ratio = dist / lastTouchDist.current;
        const newScale = clampScale(scale * ratio);

        // Get canvas-relative center
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const px = centerX - rect.left;
          const py = centerY - rect.top;
          const mousePointTo = {
            x: (px - position.x) / scale,
            y: (py - position.y) / scale,
          };
          setScale(newScale);
          setPosition({
            x: px - mousePointTo.x * newScale,
            y: py - mousePointTo.y * newScale,
          });
        }
      }

      lastTouchDist.current = dist;
      lastTouchCenter.current = { x: centerX, y: centerY };
    },
    [scale, position]
  );

  const handleTouchEnd = useCallback(() => {
    lastTouchDist.current = null;
    lastTouchCenter.current = null;
  }, []);

  // Zoom buttons
  const handleZoomIn = useCallback(() => {
    const newScale = clampScale(scale * ZOOM_STEP);
    const cx = stageSize.width / 2;
    const cy = stageSize.height / 2;
    const mousePointTo = {
      x: (cx - position.x) / scale,
      y: (cy - position.y) / scale,
    };
    setScale(newScale);
    setPosition({
      x: cx - mousePointTo.x * newScale,
      y: cy - mousePointTo.y * newScale,
    });
  }, [scale, position, stageSize]);

  const handleZoomOut = useCallback(() => {
    const newScale = clampScale(scale / ZOOM_STEP);
    const cx = stageSize.width / 2;
    const cy = stageSize.height / 2;
    const mousePointTo = {
      x: (cx - position.x) / scale,
      y: (cy - position.y) / scale,
    };
    setScale(newScale);
    setPosition({
      x: cx - mousePointTo.x * newScale,
      y: cy - mousePointTo.y * newScale,
    });
  }, [scale, position, stageSize]);

  // Room click
  const handleRoomClick = useCallback(
    room => {
      setSelectedRoom(room);
      setSelectedLevel(room.level);
      sizeTypes.forEach(sizeType => {
        if (sizeType.sizeTypes.includes(room.ItemSizeType.Name)) {
          setSelectedSizeType(sizeType);
        }
      });
    },
    [setSelectedRoom, setSelectedLevel, setSelectedSizeType, sizeTypes]
  );

  // Tooltip hover
  const handleRoomHover = useCallback((e, room) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.evt.clientX - rect.left;
    const y = e.evt.clientY - rect.top;
    setTooltip({ visible: true, x, y, room });
  }, []);

  const handleRoomHoverEnd = useCallback(() => {
    setTooltip({ visible: false, x: 0, y: 0, room: null });
  }, []);

  return (
    <div>
      <div
        ref={containerRef}
        className="relative w-full !border !border-solid border-gray-200 rounded bg-white overflow-hidden"
        style={{ height: stageSize.height }}
      >
        <MapStage
          width={stageSize.width}
          height={stageSize.height}
          scale={scale}
          position={position}
          onWheel={handleWheel}
          onDragEnd={handleDragEnd}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          cells={layout.cells}
          rooms={layout.rooms}
          onRoomClick={handleRoomClick}
          onRoomHover={handleRoomHover}
          onRoomHoverEnd={handleRoomHoverEnd}
        />
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitToView={fitToView}
        />
        <MapTooltip {...tooltip} />
      </div>
      <MapLegend />
    </div>
  );
}
